<ng-container *ngIf="isAvailable(); else notAvailable">
  <div class="di-report">
    <header class="di-report__sticky">
      <div class="di-report__breadcrumb-row">
        <div class="di-report__brand">
          <img src="/assets/images/autobrief-logo.svg" alt="AutoBrief" class="di-report__logo">
          <span class="di-report__breadcrumb">{{ t('breadcrumb') }}</span>
        </div>
        <div class="di-report__breadcrumb-actions">
          <app-v2-locale-switcher
            [locales]="localeOptions"
            [value]="locale()"
            (valueChange)="setLocale($event)">
          </app-v2-locale-switcher>
          <button
            *ngIf="isDialogMode"
            type="button"
            class="di-report__close"
            [attr.aria-label]="t('close')"
            (click)="closeDialog()">✕</button>
        </div>
      </div>

      <div class="di-report__title-row">
        <h1 class="di-report__title">
          {{ reportData()?.vehicle?.brand }} {{ reportData()?.vehicle?.model }} {{ reportData()?.vehicle?.version }}
        </h1>
        <div class="di-report__actions">
          <button type="button" [disabled]="pdfBusy()" (click)="print()">{{ t('print') }}</button>
          <button type="button" (click)="share()">{{ shareCopied() ? t('shareCopied') : t('share') }}</button>
          <button type="button" [disabled]="pdfBusy()" (click)="downloadPdf()">{{ t('export') }}</button>
        </div>
        <div class="di-report__summary">
          <p>{{ t('vin') }}: <strong>{{ reportData()?.vehicle?.vin }}</strong></p>
          <p>{{ t('reportDate') }}: <strong>{{ reportInspector()?.date ?? '—' }}</strong></p>
          <p *ngIf="reportData()?.vehicle?.locationName">{{ t('location') }}: <strong>{{ reportData()?.vehicle?.locationName }}</strong></p>
          <ng-container *ngIf="(reportData()?.totalDamagePrice ?? 0) > 0">
            <p class="di-report__total-label">{{ t('totalDamage') }}:</p>
            <p class="di-report__total-value">{{ reportData()?.totalDamagePrice | number: '1.2-2' }} €</p>
          </ng-container>
        </div>
      </div>

      <div class="di-report__chips">
        <span class="di-report__chip" *ngIf="reportData()?.vehicle?.identifier">{{ reportData()?.vehicle?.identifier }}</span>
        <span class="di-report__chip" *ngIf="reportData()?.vehicle?.ageType">{{ t('ageType_' + reportData()?.vehicle?.ageType) }}</span>
        <span class="di-report__chip" *ngIf="reportData()?.vehicle?.dateFirstRegistration">{{ reportData()?.vehicle?.dateFirstRegistration }}</span>
        <span class="di-report__chip" *ngIf="reportData()?.vehicle?.mileage">{{ reportData()?.vehicle?.mileage }} km</span>
        <span class="di-report__chip" *ngIf="reportData()?.vehicle?.fuelType">{{ t('fuelType_' + reportData()?.vehicle?.fuelType) }}</span>
        <span class="di-report__chip" *ngIf="reportData()?.vehicle?.transmissionType">{{ t('transmissionType_' + reportData()?.vehicle?.transmissionType) }}</span>
        <span class="di-report__chip" *ngIf="reportData()?.vehicle?.enginePowerKw">{{ reportData()?.vehicle?.enginePowerKw }} kW / {{ enginePowerHp() }} KM</span>
        <span class="di-report__chip" *ngIf="colorLabel()">{{ colorLabel() }}</span>
      </div>

      <div class="di-report__tags" *ngIf="tagPills().length">
        <span class="di-report__tag" *ngFor="let tag of tagPills()">{{ tag }}</span>
      </div>

      <div class="di-report__inspector-strip">
        <span>✓ {{ t('inspectedBy') }}: <strong>{{ reportInspector()?.name ?? '—' }}</strong> {{ t('inspectionDate') }}: {{ reportInspector()?.date ?? '—' }}</span>
        <span>✓ {{ t('reviewedBy') }}: <strong>{{ reportReviewer()?.name ?? '—' }}</strong> {{ t('revisionDate') }}: {{ reportReviewer()?.date ?? '—' }}</span>
      </div>
    </header>

    <section class="di-report__section" *ngIf="exteriorDamages().length">
      <h2 class="di-report__section-title">{{ t('sectionExterior') }}</h2>
      <div class="di-report__cards">
        <div class="di-report__card" *ngFor="let damage of exteriorDamages(); let damageIndex = index">
          <span class="di-report__card-badge">{{ damageIndex + 1 }}</span>
          <h3>{{ damage.side }}: {{ damage.objectConditionTemplateSidePart }}</h3>
          <span class="di-report__card-status" *ngIf="damage.status">{{ t('status_' + damage.status) }}</span>
          <div class="di-report__card-media">
            <img *ngIf="damage.damagePointImageUrlThumbnail" [src]="damage.damagePointImageUrlThumbnail" class="di-report__card-diagram" alt="">
            <!-- offset must match orderedDamages()'s exterior/interior/other order -->
            <button
              type="button"
              class="di-report__card-photo"
              *ngFor="let image of imagesWithNormal(damage); let photoIndex = index"
              (click)="openLightboxForDamage(damageIndex, photoIndex)">
              <img [src]="image.imageUrlThumbnail" alt="">
            </button>
          </div>
          <p class="di-report__card-inspector">{{ t('inspectedBy') }}<br><strong>{{ damage.inspectionUserFullName }}</strong></p>
          <p class="di-report__card-inspector">{{ t('inspectionDate') }}<br><strong>{{ damage.inspectionTime }}</strong></p>
          <p class="di-report__card-cost" *ngIf="damage.damagePrice > 0">{{ t('damageCost') }}: <strong>{{ damage.damagePrice }} €</strong></p>
          <ng-container *ngIf="damage.inspectionComment">
            <p class="di-report__card-comment-label">{{ t('comment') }}</p>
            <p class="di-report__card-comment">{{ damage.inspectionComment }}</p>
          </ng-container>
        </div>
      </div>
    </section>

    <section class="di-report__section" *ngIf="interiorDamages().length">
      <h2 class="di-report__section-title">{{ t('sectionInterior') }}</h2>
      <div class="di-report__cards">
        <div class="di-report__card" *ngFor="let damage of interiorDamages(); let damageIndex = index">
          <span class="di-report__card-badge">{{ damageIndex + 1 }}</span>
          <h3>{{ damage.side }}: {{ damage.objectConditionTemplateSidePart }}</h3>
          <span class="di-report__card-status" *ngIf="damage.status">{{ t('status_' + damage.status) }}</span>
          <div class="di-report__card-media">
            <img *ngIf="damage.damagePointImageUrlThumbnail" [src]="damage.damagePointImageUrlThumbnail" class="di-report__card-diagram" alt="">
            <!-- offset must match orderedDamages()'s exterior/interior/other order -->
            <button
              type="button"
              class="di-report__card-photo"
              *ngFor="let image of imagesWithNormal(damage); let photoIndex = index"
              (click)="openLightboxForDamage(exteriorDamages().length + damageIndex, photoIndex)">
              <img [src]="image.imageUrlThumbnail" alt="">
            </button>
          </div>
          <p class="di-report__card-inspector">{{ t('inspectedBy') }}<br><strong>{{ damage.inspectionUserFullName }}</strong></p>
          <p class="di-report__card-inspector">{{ t('inspectionDate') }}<br><strong>{{ damage.inspectionTime }}</strong></p>
          <p class="di-report__card-cost" *ngIf="damage.damagePrice > 0">{{ t('damageCost') }}: <strong>{{ damage.damagePrice }} €</strong></p>
          <ng-container *ngIf="damage.inspectionComment">
            <p class="di-report__card-comment-label">{{ t('comment') }}</p>
            <p class="di-report__card-comment">{{ damage.inspectionComment }}</p>
          </ng-container>
        </div>
      </div>
    </section>

    <section class="di-report__section" *ngIf="otherDamages().length">
      <h2 class="di-report__section-title">{{ t('sectionOther') }}</h2>
      <div class="di-report__other-grid">
        <ng-container *ngFor="let damage of otherDamages(); let damageIndex = index">
          <!-- offset must match orderedDamages()'s exterior/interior/other order -->
          <button
            type="button"
            class="di-report__other-photo"
            *ngFor="let image of imagesWithNormal(damage); let photoIndex = index"
            (click)="openLightboxForDamage(exteriorDamages().length + interiorDamages().length + damageIndex, photoIndex)">
            <img [src]="image.imageUrlThumbnail" alt="">
          </button>
        </ng-container>
      </div>
    </section>

    <section class="di-report__tyres" *ngIf="reportData()?.tyres?.length">
      <h2 class="di-report__section-title">{{ t('tyresTitle') }}</h2>
      <div class="di-report__tyres-grid">
        <div class="di-report__tyre-card" *ngFor="let tyre of reportData()?.tyres">
          <h3>{{ tyreLocationLabel(tyre) }}</h3>
          <p class="di-report__tyre-size">{{ tyre.width }}/{{ tyre.height }} R{{ tyre.diameter }}</p>
          <p>{{ tyre.tyreBrandModel }}</p>
          <p>{{ tyre.tyreType }}</p>
          <p>DOT: {{ tyre.dot }}</p>
          <p>{{ t('tyreDepth') }}: {{ tyre.depth }} mm</p>
        </div>
      </div>
    </section>

    <app-v2-lightbox [images]="lightboxImages()" [(index)]="lightboxIndex"></app-v2-lightbox>

    <footer class="di-report__footer">
      <img src="/assets/images/autobrief-logo.svg" alt="AutoBrief" class="di-report__footer-logo">
      <p>{{ t('footerCopyright') }}</p>
    </footer>
  </div>
</ng-container>
<ng-template #notAvailable>
  <div class="di-report__not-available">
    <p>{{ t('notAvailable') }}</p>
    <p>{{ reason() }}</p>
  </div>
</ng-template>
